import React, { Fragment, useEffect, useMemo } from "react";
import { useTable, useSortBy, usePagination, useExpanded } from "react-table";
import PropTypes from "prop-types";

import {
  StyledTableComponent,
  StyledTableContainer,
  StyledTable,
  TableLoadingUI,
  ThSortContainer,
  useCustomFlexLayout,
  NoDataComponent,
  RowNumbers,
} from "./ReactTableStyles";
import { PaginationComponent } from "./ReactTablePagination";

/**
 * @param {{
 * tableTheme: "default" | "genericTheme" | "casesTableTheme" |"housingSubRowTheme" | "linkageDeletionTheme";
 * }} props Props for tableTheme
 */

// Column Cell Props:
// isButton | centered | id === "checkbox" (helps center positions)
// disableSortBy (prevents specific columns from being sorted, ie. checkboxes, buttons )
// width | minWidth | maxWidth | fixedWidth

export const ReactTable = ({
  columns, // required
  data, // required
  fetchData,
  loading,
  searchTerm,
  defaultSortBy = [],
  tableStyle = {},
  tableTheme = "default",
  tableClassName,
  showRowNumbers = false,
  // Pagination props
  pageSizeOptions,
  striped = false, // striped rows
  defaultPageSize = 10,
  showPagination = false, // display table paginator on bottom
  manualPagination, // server-side handled pagination
  // pageCount - optional; passed in dynamically inside "...rest" below due to known react-table v7 pagination bug. Creates issue with non-manual pagination. Only pass in pageCount if manualPagination.

  // Expanded rows
  renderRowSubComponent,
  allExpanded,
  isExpandedRow,

  // Cases tables
  onDataFetch = () => {}, // Executes function after data fetch, useful for resetting/modifying states
  getPaginationProps = () => {}, // Returns "pageIndex", "pageSize" props. ie. used in counter components in CasesTable and NotificationTable.
  refetchToFirstPageDependencies = [], // Custom dependencies to fetch data (refreshes to first page)
  refetchDependencies = [], // Custom dependencies to fetch data (stays on page)

  // noDataComp
  noDataComponent = () => {},
  noDataComponentText,
  // Sorting props
  allowUnsortedState, // if true, unsorted state is available
  manualSortBy,

  ...rest // ie. pageCount
}) => {
  // Safety net for undefined/null data (like 790A reuse, bad practice).
  data = useMemo(() => {
    return data ? data : [];
  }, [data]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    allColumns,
    state: { pageIndex, pageSize, sortBy },
  } = useTable(
    {
      columns,
      data,
      // fetchData() = server-side pagination/sort = manualPagination/manualSortBy true
      manualPagination: manualPagination || fetchData,
      manualSortBy: manualSortBy || fetchData,
      disableSortRemove: !allowUnsortedState || false,
      disableMultiSort: true,
      autoResetPage: false,
      initialState: {
        pageSize: defaultPageSize,
        sortBy: defaultSortBy,
        hiddenColumns: columns
          .filter((column) => {
            return column.show === false;
          })
          .map((column) => column.id),
      },
      ...rest,
    },
    useSortBy,
    useExpanded,
    usePagination,
    useCustomFlexLayout
  );

  useEffect(() => {
    if (fetchData) {
      fetchData({
        pageIndex,
        pageSize,
        sortBy,
        searchTerm,
      });
      onDataFetch();
    }
    getPaginationProps({ pageIndex, pageSize });
    // Ideally use useCallbacks in parent in future. Currently cannot do this due to bad react standards on some components.
    // (eslint is just letting us know it can't check statically)
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [pageIndex, pageSize, ...refetchDependencies]);

  // Refetches to first page
  useEffect(() => {
    if (fetchData) {
      fetchData({
        // pageIndex: 0 prevents double-fetching when not on page 0
        pageIndex: 0,
        pageSize,
        sortBy,
        searchTerm,
      });
      onDataFetch();
      gotoPage(0);
    }
    // Ideally use useCallbacks in parent in future.
    // (eslint is just letting us know it can't check statically)
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [sortBy, searchTerm, ...refetchToFirstPageDependencies]);

  return (
    <StyledTableComponent tableTheme={tableTheme} style={tableStyle}>
      <TableLoadingUI loading={loading}>Loading...</TableLoadingUI>
      <StyledTableContainer>
        <StyledTable
          {...getTableProps()}
          striped={striped}
          className={tableClassName}
          showRowNumbers={showRowNumbers}
        >
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th
                    {...column?.getHeaderProps(column?.getSortByToggleProps())}
                  >
                    {column?.canSort ? (
                      <ThSortContainer
                        key={column?.id}
                        isSorted={column?.isSorted}
                        isSortedDesc={column?.isSortedDesc}
                      >
                        {column?.render("Header")}
                      </ThSortContainer>
                    ) : (
                      column?.render("Header")
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              const isDisabled = row.original.disabled;
              return (
                <Fragment key={row?.id}>
                  <tr {...row?.getRowProps()} key={row?.id}>
                    {showRowNumbers && (
                      <RowNumbers>{row?.index + 1}</RowNumbers>
                    )}
                    {row?.cells?.map((cell) => {
                      const renderUnstyledCell =
                        cell?.column?.isButton ||
                        cell?.column?.centered ||
                        cell?.column?.id === "checkbox";
                      const renderPopoverCell =
                        cell?.column?.type === "popover";
                      return (
                        <td
                          {...cell?.getCellProps()}
                          style={{
                            ...cell.getCellProps().style,
                            backgroundColor: isDisabled ? "#f0f0f0" : "inherit",
                            color: isDisabled ? "#999" : "inherit",
                            //pointerEvents: isDisabled ? "none" : "auto",
                            opacity: isDisabled ? 0.6 : 1,
                          }}
                        >
                          {renderUnstyledCell && cell?.render("Cell")}
                          {renderPopoverCell && (
                            <div className="cell-popover">
                              {cell?.render("Cell")}
                            </div>
                          )}
                          {!renderUnstyledCell && !renderPopoverCell && (
                            <div className="cell-content">
                              {cell?.render("Cell")}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                  {(row?.isExpanded || allExpanded) && (
                    <tr>
                      <td colSpan={allColumns?.length} style={{ padding: "0" }}>
                        {renderRowSubComponent(row)}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {page?.length === 0 &&
              (noDataComponent() || (
                <NoDataComponent NoDataComponent={NoDataComponent}>
                  {noDataComponentText || ""}
                </NoDataComponent>
              ))}
          </tbody>
        </StyledTable>
      </StyledTableContainer>
      {(showPagination || pageSizeOptions || data?.length > page?.length) &&
        page?.length !== 0 && (
          <PaginationComponent
            pageIndex={pageIndex}
            pageOptions={pageOptions}
            pageSize={pageSize}
            setPageSize={setPageSize}
            pageSizeOptions={pageSizeOptions}
            previousPage={previousPage}
            nextPage={nextPage}
            canPreviousPage={canPreviousPage}
            canNextPage={canNextPage}
            gotoPage={gotoPage}
            isExpandedRow={isExpandedRow}
          />
        )}
    </StyledTableComponent>
  );
};

ReactTable.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array,
  fetchData: PropTypes.func,
  loading: PropTypes.bool,
  searchTerm: PropTypes.string,
  defaultSortBy: PropTypes.array,
  tableStyle: PropTypes.object,
  tableTheme: PropTypes.oneOf([
    "default",
    "genericTheme",
    "casesTableTheme",
    "housingSubRowTheme",
    "linkageDeletionTheme",
  ]),
  tableClassName: PropTypes.string,

  // Pagination props
  pageCount: PropTypes.number,
  pageSizeOptions: PropTypes.array,
  striped: PropTypes.bool,
  defaultPageSize: PropTypes.number,
  showPagination: PropTypes.bool,
  manualPagination: PropTypes.bool,

  // Expanded rows
  renderRowSubComponent: PropTypes.func,
  allExpanded: PropTypes.bool,
  isExpandedRow: PropTypes.bool,

  // Cases tables
  onDataFetch: PropTypes.func,
  getPaginationProps: PropTypes.func,
  refetchToFirstPageDependencies: PropTypes.array,
  refetchDependencies: PropTypes.array,
  // noDataComp
  noDataComponent: PropTypes.func,
  noDataComponentText: PropTypes.string,

  // Sorting props
  allowUnsortedState: PropTypes.bool,
  manualSortBy: PropTypes.bool,
};
